import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

interface AccessibilityProps {
  className?: string;
  isOpen: boolean;
  onClose: () => void;
}

const Accessibility: React.FC<AccessibilityProps> = ({ className = '', isOpen, onClose }) => {
  // State for accessibility settings
  const [fontSize, setFontSize] = useState(
    localStorage.getItem('accessibility-fontSize') || 'normal'
  );
  const [contrast, setContrast] = useState(
    localStorage.getItem('accessibility-contrast') || 'normal'
  );
  const [reducedMotion, setReducedMotion] = useState(
    localStorage.getItem('accessibility-reducedMotion') === 'true'
  );

  // Apply accessibility settings
  useEffect(() => {
    // Font size
    document.documentElement.classList.remove('text-sm', 'text-base', 'text-lg', 'text-xl');
    if (fontSize === 'small') document.documentElement.classList.add('text-sm');
    if (fontSize === 'large') document.documentElement.classList.add('text-lg');
    if (fontSize === 'x-large') document.documentElement.classList.add('text-xl');
    localStorage.setItem('accessibility-fontSize', fontSize);

    // Contrast
    document.documentElement.classList.remove('high-contrast', 'inverted');
    if (contrast === 'high') document.documentElement.classList.add('high-contrast');
    if (contrast === 'inverted') document.documentElement.classList.add('inverted');
    localStorage.setItem('accessibility-contrast', contrast);

    // Reduced motion
    if (reducedMotion) {
      document.documentElement.classList.add('reduce-motion');
    } else {
      document.documentElement.classList.remove('reduce-motion');
    }
    localStorage.setItem('accessibility-reducedMotion', reducedMotion.toString());
  }, [fontSize, contrast, reducedMotion]);

  // Check for user preferences on initial load
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion && !localStorage.getItem('accessibility-reducedMotion')) {
      setReducedMotion(true);
    }
  }, []);

  return (
    <div className={`relative ${className}`}>
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black pointer-events-auto z-40"
              onClick={onClose}
            />
            
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-lg shadow-xl p-6 z-50 pointer-events-auto"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Accessibility Options</h3>
                <button
                  onClick={onClose} 
                  className="text-gray-500 hover:text-gray-700 focus:outline-none">
                  <FontAwesomeIcon icon={faTimes} className="h-6 w-6" />
                </button>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Font Size</label>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setFontSize('small')}
                    className={`px-3 py-2 rounded text-sm ${fontSize === 'small' ? 'bg-brand-green-500 text-white' : 'bg-gray-100 text-gray-800'}`}
                  >
                    Small
                  </button>
                  <button
                    type="button"
                    onClick={() => setFontSize('normal')}
                    className={`px-3 py-2 rounded text-sm ${fontSize === 'normal' ? 'bg-brand-green-500 text-white' : 'bg-gray-100 text-gray-800'}`}
                  >
                    Normal
                  </button>
                  <button
                    type="button"
                    onClick={() => setFontSize('large')}
                    className={`px-3 py-2 rounded text-sm ${fontSize === 'large' ? 'bg-brand-green-500 text-white' : 'bg-gray-100 text-gray-800'}`}
                  >
                    Large
                  </button>
                  <button
                    type="button"
                    onClick={() => setFontSize('x-large')}
                    className={`px-3 py-2 rounded text-sm ${fontSize === 'x-large' ? 'bg-brand-green-500 text-white' : 'bg-gray-100 text-gray-800'}`}
                  >
                    X-Large
                  </button>
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Contrast</label>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setContrast('normal')}
                    className={`px-3 py-2 rounded text-sm ${contrast === 'normal' ? 'bg-brand-green-500 text-white' : 'bg-gray-100 text-gray-800'}`}
                  >
                    Normal
                  </button>
                  <button
                    type="button"
                    onClick={() => setContrast('high')}
                    className={`px-3 py-2 rounded text-sm ${contrast === 'high' ? 'bg-brand-green-500 text-white' : 'bg-gray-100 text-gray-800'}`}
                  >
                    High Contrast
                  </button>
                  <button
                    type="button"
                    onClick={() => setContrast('inverted')}
                    className={`px-3 py-2 rounded text-sm ${contrast === 'inverted' ? 'bg-brand-green-500 text-white' : 'bg-gray-100 text-gray-800'}`}
                  >
                    Inverted
                  </button>
                </div>
              </div>
              
              <div className="mb-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={reducedMotion}
                    onChange={(e) => setReducedMotion(e.target.checked)}
                    className="h-5 w-5 text-brand-green-600 focus:ring-brand-green-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Reduce motion</span>
                </label>
              </div>
              
              <button
                type="button"
                onClick={onClose}
                className="w-full btn btn-primary"
              >
                Save & Close
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Accessibility;